import { MooAnalyticsEvents } from '@cowbell/data/analytics';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@cowbell/ui/core';
import {
  faCheck,
  faCopy,
  faPrint,
  faThumbsDown as faThumbsDownLight,
  faThumbsUp as faThumbsUpLight,
} from '@fortawesome/pro-light-svg-icons';
import {
  faThumbsDown as faThumbsDownSolid,
  faThumbsUp as faThumbsUpSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { PANEL_OVERLAY_Z } from '../LayoutContainer/LayoutContainer';
import { useMooConfig } from '../../provider';
import type { FeedbackRating } from '../../types/transport';
import { faBrilliant } from './faBrilliant';
import styles from './MessageFeedback.module.css';

type Rating = FeedbackRating | null;

interface RatingButtonConfig {
  readonly rating: FeedbackRating;
  /** Tooltip text. */
  readonly tooltip: string;
  /** Accessible name — kept distinct from the tooltip for the thumbs so
   *  existing hosts/tests keyed on "Thumbs up" / "Thumbs down" keep working. */
  readonly ariaLabel: string;
  /** Mantine color used once this rating is the active one. */
  readonly activeColor: string;
  readonly renderIcon: (active: boolean) => ReactNode;
}

/** The three one-shot ratings, in display order: Brilliant leads the
 *  cluster, then thumbs up / thumbs down. */
const RATING_BUTTONS: readonly RatingButtonConfig[] = [
  {
    rating: 'brilliant',
    tooltip: 'Brilliant',
    ariaLabel: 'Brilliant',
    activeColor: 'blue',
    renderIcon: () => <FontAwesomeIcon icon={faBrilliant} fontSize={14} />,
  },
  {
    rating: 'up',
    tooltip: 'Like',
    ariaLabel: 'Thumbs up',
    activeColor: 'green',
    renderIcon: (active) => (
      <FontAwesomeIcon
        icon={active ? faThumbsUpSolid : faThumbsUpLight}
        fontSize={12}
      />
    ),
  },
  {
    rating: 'down',
    tooltip: 'Dislike',
    ariaLabel: 'Thumbs down',
    activeColor: 'red',
    renderIcon: (active) => (
      <FontAwesomeIcon
        icon={active ? faThumbsDownSolid : faThumbsDownLight}
        fontSize={12}
      />
    ),
  },
];

/** Comment-panel prompt per pending rating. A lookup rather than nested
 *  ternaries (repo rule). */
const PROMPT_BY_RATING: Record<FeedbackRating, string> = {
  up: 'Any additional feedback?',
  down: 'What went wrong?',
  brilliant: 'What made this brilliant?',
};

/** Only a down-vote requires a comment; up and brilliant are optional. */
function requiresComment(rating: FeedbackRating): boolean {
  return rating === 'down';
}

export interface MessageFeedbackProps {
  readonly messageId: string;
  /** Persisted rating loaded from history. Undefined for fresh messages. */
  readonly initialRating?: Rating;
  /** Raw assistant text used by Copy + Print actions. Optional so callers
   *  that only want the thumbs cluster (e.g. transient previews) can pass
   *  nothing — the Copy/Print buttons hide themselves when no text. */
  readonly text?: string;
  /** Ref to the rendered markdown DOM node (set by MessageBubble around its
   *  ReactMarkdown output). Print reuses `innerHTML` from here so the popup
   *  shows formatted HTML instead of raw markdown syntax; falls back to
   *  escaped plain text when absent. */
  readonly contentRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Thumbs-up / thumbs-down / brilliant affordance on assistant messages.
 *
 * - Three one-shot ratings; once any is recorded (or hydrated from history)
 *   all three lock.
 * - Down-vote requires a non-empty comment; up and brilliant comments are
 *   optional.
 * - "Brilliant" is the "exceptionally good answer" signal — it travels the
 *   same transport as the thumbs and lands on the same Langfuse trace.
 * - Optimistic UI; revert on transport error.
 */
export function MessageFeedback({
  messageId,
  initialRating = null,
  text,
  contentRef,
}: MessageFeedbackProps) {
  const { transport, logger, analytics, toast } = useMooConfig();
  const [rating, setRating] = useState<Rating>(initialRating);
  const [pendingType, setPendingType] = useState<FeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Briefly flash a checkmark after a successful copy. The timer is scoped
  // to the `copied=true` state so back-to-back copies always show the flash.
  useEffect(() => {
    if (!copied) return;
    const id = globalThis.setTimeout(() => setCopied(false), 1500);
    return () => globalThis.clearTimeout(id);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (err) {
      logger.error('moo.copy.error', { messageId, error: String(err) });
      toast?.error("Couldn't copy to clipboard");
    }
  }, [text, logger, messageId, toast]);

  const handlePrint = useCallback(() => {
    if (!text) return;
    const printWindow = globalThis.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      toast?.error('Pop-up blocked — allow pop-ups to print.');
      return;
    }
    const renderedHtml = contentRef?.current?.innerHTML;
    // No rendered DOM to reuse — escapeHtml() only neutralizes markup, it
    // doesn't preserve whitespace, so plain HTML would collapse newlines.
    // `white-space: pre-wrap` on the fallback wrapper keeps line breaks
    // intact without pulling in the code-block styling that a bare `<pre>`
    // would apply via PRINT_CSS.
    const printContent =
      renderedHtml ?? `<div class="fallback-text">${escapeHtml(text)}</div>`;
    const timestamp = new Date().toLocaleString();
    // `document.write` + `document.close` (not DOM APIs) re-triggers the
    // popup's load cycle so `onload` reliably fires below — an `about:blank`
    // popup from `window.open('')` is already "loaded" by the time it
    // returns, so building the doc via createElement/appendChild alone never
    // fires `load` and print() would never be called.
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Ask Moo</title>
  <style>${PRINT_CSS}</style>
</head>
<body>
  <div class="print-header">Ask Moo &mdash; ${timestamp}</div>
  <div class="response-label">Response</div>
  <div>${printContent}</div>
</body>
</html>`);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }, [text, contentRef, toast]);

  const closePopover = useCallback(() => {
    setPendingType(null);
    setComment('');
  }, []);

  // The last message is the usual rating target and the panel grows it
  // downward, past the list's auto-scroll (which only follows new content).
  // Bring the freshly mounted panel into view so Send is never off-screen.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (pendingType === null) return;
    const el = panelRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [pendingType]);

  useEffect(() => {
    if (pendingType === null) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      closePopover();
    };
    globalThis.addEventListener('keydown', handleEscape, true);
    return () => globalThis.removeEventListener('keydown', handleEscape, true);
  }, [pendingType, closePopover]);

  const submit = useCallback(
    async (next: Rating, withdrawalComment: string | undefined) => {
      const previous = rating;
      setRating(next);
      setSubmitting(true);
      try {
        await transport.submitFeedback({
          messageId,
          rating: next,
          comment: withdrawalComment,
        });
        const trackedType = next ?? 'withdrawn';
        const hasComment = (withdrawalComment ?? '').trim().length > 0;
        logger.debug('moo.feedback.submitted', {
          messageId,
          type: trackedType,
        });
        analytics.track(MooAnalyticsEvents.FEEDBACK_SUBMITTED, {
          messageId,
          type: trackedType,
          hasComment,
        });
      } catch (err) {
        logger.error('moo.feedback.error', {
          messageId,
          error: String(err),
        });
        toast?.error("Couldn't submit feedback");
        setRating(previous);
      } finally {
        setSubmitting(false);
      }
    },
    [rating, transport, messageId, logger, analytics, toast]
  );

  const handleRatingClick = useCallback(
    (type: FeedbackRating) => {
      // One-shot: once a rating is recorded, all rating buttons lock. Prevents
      // the user from spam-clicking and firing a chain of submitFeedback calls.
      // (V1 lets you re-click the active thumb to withdraw; we drop that
      // here on purpose — a single explicit rating per message is enough.)
      if (rating !== null) return;
      setPendingType(type);
      setComment('');
    },
    [rating]
  );

  const handleSend = useCallback(() => {
    if (!pendingType) return;
    const trimmed = comment.trim();
    submit(pendingType, trimmed.length > 0 ? trimmed : undefined).catch(
      () => undefined
    );
    closePopover();
  }, [pendingType, comment, submit, closePopover]);

  const commentRequired = pendingType !== null && requiresComment(pendingType);
  const canSubmit = commentRequired ? comment.trim().length > 0 : true;
  const ratingsLocked = submitting || rating !== null;

  return (
    <Box>
      {/* `flex-end` matters only while the comment panel is open: the panel
       *  widens this box past the icon row, and the cluster has to stay on
       *  the footer's right edge rather than drifting left. */}
      <Group gap={4} justify="flex-end">
        {RATING_BUTTONS.map((cfg) => (
          <FeedbackButton
            key={cfg.rating}
            config={cfg}
            active={rating === cfg.rating}
            disabled={ratingsLocked}
            onClick={() => handleRatingClick(cfg.rating)}
          />
        ))}
        {text && (
          <Tooltip label={copied ? 'Copied' : 'Copy'} zIndex={PANEL_OVERLAY_Z}>
            <ActionIcon
              variant="subtle"
              size="sm"
              color={copied ? 'green' : 'gray'}
              aria-label="Copy message"
              onClick={handleCopy}
            >
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} fontSize={12} />
            </ActionIcon>
          </Tooltip>
        )}
        {text && (
          <Tooltip label="Print" zIndex={PANEL_OVERLAY_Z}>
            <ActionIcon
              variant="subtle"
              size="sm"
              color="gray"
              aria-label="Print message"
              onClick={handlePrint}
            >
              <FontAwesomeIcon icon={faPrint} fontSize={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>

      {pendingType !== null && (
        <Stack
          ref={panelRef}
          data-testid="feedback-comment-panel"
          gap="xs"
          mt="xs"
          p="xs"
          style={{
            background: 'var(--mantine-color-gray-0)',
            borderRadius: 8,
            maxWidth: 360,
          }}
        >
          <Text size="sm" fw={500}>
            {PROMPT_BY_RATING[pendingType]}
          </Text>
          <Textarea
            autosize
            minRows={2}
            maxRows={6}
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            aria-label="Feedback comment"
            placeholder={commentRequired ? 'Required' : 'Optional'}
          />
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={closePopover}>
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={handleSend}
              disabled={!canSubmit || submitting}
            >
              Send
            </Button>
          </Group>
        </Stack>
      )}
    </Box>
  );
}

interface FeedbackButtonProps {
  readonly config: RatingButtonConfig;
  readonly active: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
}

/** One rating button in the cluster. Idle buttons are gray; the active one
 *  takes its config's color so the recorded rating reads at a glance. */
function FeedbackButton({
  config,
  active,
  disabled,
  onClick,
}: FeedbackButtonProps) {
  // Portalled to body: needs a z-index above the floating/fullscreen panel
  // or the tooltip paints behind the panel (see PANEL_OVERLAY_Z).
  return (
    <Tooltip label={config.tooltip} zIndex={PANEL_OVERLAY_Z}>
      <ActionIcon
        variant="subtle"
        size="sm"
        color={active ? config.activeColor : 'gray'}
        className={active ? styles.activeRating : undefined}
        aria-label={config.ariaLabel}
        aria-pressed={active}
        disabled={disabled}
        onClick={onClick}
      >
        {config.renderIcon(active)}
      </ActionIcon>
    </Tooltip>
  );
}

/** Escapes text for safe injection into the print document's HTML string.
 *  Used as the fallback when no rendered `contentRef` DOM is available —
 *  the sanctioned path is reusing already-sanitized ReactMarkdown output;
 *  this only ever emits literal characters, never live markup. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Ported from V1 (`MessageActions.tsx`) for print-output parity. */
const PRINT_CSS = `
    *, *::before, *::after { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      font-size: 1.075rem;
      line-height: 1.6;
      color: #1a1a1a;
      background: #fff;
      margin: 0;
      padding: 2rem;
    }
    .print-header {
      font-size: 0.95rem;
      color: #888;
      border-bottom: 1px solid #ddd;
      padding-bottom: 0.5rem;
      margin-bottom: 1.5rem;
    }
    .response-label {
      font-size: 0.8875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #888;
      margin-bottom: 0.5rem;
    }
    h1 { font-size: 1.45rem; font-weight: 600; margin: 1rem 0 0.5rem; }
    h2 { font-size: 1.325rem; font-weight: 600; margin: 0.875rem 0 0.375rem; }
    h3 { font-size: 1.2rem; font-weight: 600; margin: 0.75rem 0 0.375rem; }
    h4, h5, h6 { font-size: 1.075rem; font-weight: 600; margin: 0.5rem 0 0.25rem; }
    p { margin: 0.5rem 0; }
    strong { font-weight: 600; }
    ul, ol { margin: 0.5rem 0; padding-left: 1.5rem; }
    li { margin: 0.25rem 0; }
    /* MarkdownTable's wrapper Box carries inline overflow-x:auto/max-width
     * (for in-app horizontal scrolling), which would otherwise clip wide
     * tables when printed — inline styles beat a plain class selector, so
     * this needs !important to win, and targets the data attribute since
     * the wrapper never gets a class named "table-wrapper". */
    [data-print-table-wrapper] {
      overflow-x: visible !important;
      max-width: none !important;
      width: 100%;
    }
    table { width: 100%; border-collapse: collapse; margin: 0.75rem 0; font-size: 1.0125rem; }
    th, td { border: 1px solid #ccc; padding: 0.4rem 0.6rem; text-align: left; }
    th { font-weight: 600; background: #f5f5f5; }
    pre { background: #f5f5f5; padding: 0.75rem; border-radius: 0.375rem; overflow-x: auto; font-size: 1.0125rem; }
    code { font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 0.85em; }
    blockquote {
      font-size: 1.0125rem;
      color: #666;
      margin: 0.25rem 0;
      padding-left: 0.75rem;
      border-left: 2px solid #ddd;
    }
    blockquote p { margin: 0; }
    svg:not(.recharts-surface) { display: none !important; }
    /* Hides interactive chrome (copy buttons, etc). Citation chips render
     * as real <button> elements too, so they're explicitly exempted via
     * [data-print-visible] — otherwise resolved citations would vanish
     * from the printed page while unresolved ones (plain spans) remain. */
    button:not([data-print-visible]),
    [role="button"]:not([data-print-visible]) { display: none !important; }
    .fallback-text { white-space: pre-wrap; word-wrap: break-word; }
    img { max-width: 100%; }
    @media print {
      body { padding: 0; }
      th { background: #f5f5f5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
      thead { display: table-header-group; }
    }`;
